// src/components/modals/AddSubTaskModal.tsx
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { useTaskStore } from '@/store/taskStore';

const subTaskSchema = z.object({
  task: z.string().trim().min(1, 'Task is required'),
  subTask: z.string().trim().min(1, 'Sub-task is required'),
});

type SubTaskFormData = z.infer<typeof subTaskSchema>;

interface AddSubTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddSubTaskModal: React.FC<AddSubTaskModalProps> = ({ isOpen, onClose }) => {
  const { tasks = [], fetchTasks } = useTaskStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<SubTaskFormData>({
    resolver: zodResolver(subTaskSchema),
    defaultValues: {
      task: '',
      subTask: '',
    }
  });

  useEffect(() => {
    if (isOpen) {
      fetchTasks({
        limit: 100,
        isActive: true
      });
    }
  }, [isOpen, fetchTasks]);

  const onSubmit = async (data: SubTaskFormData) => {
    try {
      console.log('Sub-task data:', data);
      // TODO: Replace with actual API call to add sub-task to the selected task
      // await addSubTask(data.task, data.subTask);
      
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to create sub-task:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Sub-task" size="md">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 px-3">
        {/* Sub-task Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sub-task
          </label>
          <Input
            {...register('subTask')}
            placeholder="Enter sub-task name"
            error={errors.subTask?.message}
          />
        </div>
       
        {/* Task Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Task
          </label>
          <Select 
            {...register('task')} 
            error={errors.task?.message}
          >
            <option value="">Select Task</option>
            {tasks.map(task => (
              <option key={task._id || task.id} value={task._id || task.id}>
                {task.task}
              </option>
            ))}
          </Select>
        </div>

       

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button 
            variant="secondary" 
            type="button" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
};